import { useQuery } from '@tanstack/react-query'
import { formatPercent } from '@/utils/format'
import { BadgePercent, FolderTree, Pencil, Plus, Power, Receipt, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { errorMessage } from '@/api/errors'
import { EmptyState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { FilterBar, SearchInput } from '@/components/tables/FilterBar'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { classesQuery, yearsQuery } from '@/features/academic/api'
import { useAuth } from '@/features/auth/useAuth'
import {
  categoriesQuery,
  discountsQuery,
  feeItemsQuery,
  useDeleteCategory,
  useDeleteDiscount,
  useDeleteFeeItem,
  useSetFeeItemActive,
} from '@/features/finance/api'
import { CategoryDialog, DiscountDialog, FeeItemDialog } from '@/features/finance/components/SetupDialogs'
import { frequencyLabel } from '@/features/finance/status'
import { FEE_FREQUENCIES, type FeeCategory, type FeeDiscount, type FeeItem } from '@/features/finance/types'
import { useMoney } from '@/features/finance/useCurrency'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

type Tab = 'categories' | 'items' | 'discounts'
type Editing<T> = { record: T | null } | null

function ActiveBadge({ active }: { active: boolean }) {
  const { t } = useTranslation('finance')
  return (
    <Badge tone={active ? 'success' : 'neutral'} size="sm" dot>
      {active ? t('Active') : t('Inactive')}
    </Badge>
  )
}

function CategoriesTab() {
  const { t } = useTranslation('finance')
  const { can } = useAuth()
  const list = useQuery(categoriesQuery())
  const remove = useDeleteCategory()
  const [editing, setEditing] = useState<Editing<FeeCategory>>(null)
  const [deleting, setDeleting] = useState<FeeCategory | null>(null)
  const manage = can('finance.manage')

  const columns: Column<FeeCategory>[] = [
    { key: 'name', header: t('Name'), cell: (c) => <span className="font-medium text-ink">{c.name}</span> },
    {
      key: 'items',
      header: t('Fee items'),
      align: 'right',
      cell: (c) => <span className="tabular">{c.itemCount}</span>,
    },
    { key: 'status', header: t('Status'), cell: (c) => <ActiveBadge active={c.isActive} /> },
    ...(manage
      ? [
          {
            key: 'actions',
            header: <span className="sr-only">{t('Actions')}</span>,
            align: 'right' as const,
            cell: (c: FeeCategory) => (
              <div className="flex justify-end gap-1">
                <IconButton
                  label={t('Edit {{name}}', { name: c.name })}
                  icon={<Pencil />}
                  onClick={() => setEditing({ record: c })}
                />
                <IconButton
                  label={
                    c.itemCount > 0
                      ? t('{{name}} has fee items and cannot be deleted', { name: c.name })
                      : t('Delete {{name}}', { name: c.name })
                  }
                  icon={<Trash2 />}
                  disabled={c.itemCount > 0}
                  onClick={() => setDeleting(c)}
                />
              </div>
            ),
          },
        ]
      : []),
  ]

  return (
    <Card>
      <CardHeader
        title={t('Categories')}
        description={t('Group fee items on invoices and in reports.')}
        icon={<FolderTree />}
        actions={
          manage ? (
            <Button size="sm" leftIcon={<Plus />} onClick={() => setEditing({ record: null })}>
              {t('New category')}
            </Button>
          ) : null
        }
      />
      <DataTable
        caption={t('Fee categories')}
        columns={columns}
        rows={list.data}
        getRowId={(c) => c.id}
        loading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        skeletonRows={4}
        empty={
          <EmptyState
            icon={<FolderTree />}
            title={t('No categories yet')}
            description={t('Create categories such as Tuition, Transport or Exams.')}
            compact
          />
        }
      />
      {editing ? <CategoryDialog open onOpenChange={() => setEditing(null)} record={editing.record} /> : null}
      {deleting ? (
        <ConfirmDialog
          open
          onOpenChange={() => setDeleting(null)}
          title={t('Delete “{{name}}”?', { name: deleting.name })}
          description={t('Only empty categories can be deleted.')}
          confirmLabel={t('Delete')}
          loading={remove.isPending}
          onConfirm={() =>
            remove.mutate(deleting.id, {
              onSuccess: () => {
                toast.success(t('Category deleted'), deleting.name)
                setDeleting(null)
              },
              onError: (e) => toast.error(t('Could not delete'), errorMessage(e)),
            })
          }
        />
      ) : null}
    </Card>
  )
}

function ItemsTab() {
  const { t } = useTranslation('finance')
  const { can } = useAuth()
  const { money } = useMoney()
  const years = useQuery(yearsQuery())
  const classes = useQuery(classesQuery())
  const categories = useQuery(categoriesQuery())
  const [filters, setFilters] = useState({ academicYearId: '', categoryId: '', classId: '', search: '' })
  const yearId = filters.academicYearId || String(years.data?.find((y) => y.isCurrent)?.id ?? '')
  const list = useQuery({
    ...feeItemsQuery({ ...filters, academicYearId: yearId }),
    enabled: years.isSuccess,
  })
  const setActive = useSetFeeItemActive()
  const remove = useDeleteFeeItem()
  const [editing, setEditing] = useState<Editing<FeeItem>>(null)
  const [deleting, setDeleting] = useState<FeeItem | null>(null)
  const manage = can('finance.manage')

  const toggle = (item: FeeItem) =>
    setActive.mutate(
      { id: item.id, isActive: !item.isActive },
      {
        onSuccess: (r) =>
          toast.success(r.isActive ? t('Fee item activated') : t('Fee item deactivated'), r.name),
        onError: (e) => toast.error(t('Could not update'), errorMessage(e)),
      },
    )

  const columns: Column<FeeItem>[] = [
    {
      key: 'name',
      header: t('Item'),
      cell: (i) => (
        <div className="min-w-0">
          <span className="block font-medium text-ink">{i.name}</span>
          <span className="block text-xs text-muted">
            {i.category.name}
            {!i.isMandatory ? ` · ${t('optional')}` : ''}
          </span>
        </div>
      ),
    },
    {
      key: 'class',
      header: t('Class'),
      hideBelow: 'md',
      cell: (i) => i.schoolClass?.name ?? <span className="text-muted">{t('All classes')}</span>,
    },
    {
      key: 'frequency',
      header: t('Frequency'),
      hideBelow: 'sm',
      cell: (i) => (
        <span>
          {t(frequencyLabel[i.frequency])}
          {i.dueDay ? (
            <span className="ml-1 text-xs text-muted">· {t('due day {{day}}', { day: i.dueDay })}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'amount',
      header: t('Amount'),
      align: 'right',
      cell: (i) => <span className="font-medium tabular">{money(i.amount)}</span>,
    },
    { key: 'status', header: t('Status'), hideBelow: 'sm', cell: (i) => <ActiveBadge active={i.isActive} /> },
    ...(manage
      ? [
          {
            key: 'actions',
            header: <span className="sr-only">{t('Actions')}</span>,
            align: 'right' as const,
            cell: (i: FeeItem) => (
              <div className="flex justify-end gap-1">
                <IconButton
                  label={t('Edit {{name}}', { name: i.name })}
                  icon={<Pencil />}
                  onClick={() => setEditing({ record: i })}
                />
                <IconButton
                  label={
                    i.isActive
                      ? t('Deactivate {{name}}', { name: i.name })
                      : t('Activate {{name}}', { name: i.name })
                  }
                  icon={<Power />}
                  onClick={() => toggle(i)}
                />
                {i.invoicedCount === 0 ? (
                  <IconButton
                    label={t('Delete {{name}}', { name: i.name })}
                    icon={<Trash2 />}
                    onClick={() => setDeleting(i)}
                  />
                ) : null}
              </div>
            ),
          },
        ]
      : []),
  ]

  return (
    <Card>
      <CardHeader
        title={t('Fee items')}
        description={t(
          'Frequency decides which invoice runs bill an item. Invoiced items can only be deactivated.',
        )}
        icon={<Receipt />}
        actions={
          manage ? (
            <Button size="sm" leftIcon={<Plus />} onClick={() => setEditing({ record: null })}>
              {t('New fee item')}
            </Button>
          ) : null
        }
      />
      <FilterBar>
        <SearchInput
          value={filters.search}
          onChange={(v) => setFilters((f) => ({ ...f, search: v }))}
          placeholder={t('Search items')}
          label={t('Search fee items')}
        />
        <Select
          aria-label={t('Academic year')}
          className="sm:w-auto sm:min-w-40"
          value={yearId}
          onChange={(e) => setFilters((f) => ({ ...f, academicYearId: e.target.value }))}
          options={(years.data ?? []).map((y) => ({ value: y.id, label: y.name }))}
        />
        <Select
          aria-label={t('Filter by category')}
          className="sm:w-auto sm:min-w-40"
          placeholder={t('All categories')}
          value={filters.categoryId}
          onChange={(e) => setFilters((f) => ({ ...f, categoryId: e.target.value }))}
          options={(categories.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
        />
        <Select
          aria-label={t('Filter by class')}
          className="sm:w-auto sm:min-w-40"
          placeholder={t('All classes')}
          value={filters.classId}
          onChange={(e) => setFilters((f) => ({ ...f, classId: e.target.value }))}
          options={(classes.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
        />
      </FilterBar>
      <DataTable
        caption={t('Fee items')}
        columns={columns}
        rows={list.data}
        getRowId={(i) => i.id}
        loading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        mobileCard={(i) => (
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-ink">{i.name}</p>
              <p className="truncate text-xs text-muted">
                {i.category.name} · {i.schoolClass?.name ?? t('All classes')} ·{' '}
                {t(frequencyLabel[i.frequency])}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold tabular">{money(i.amount)}</p>
              <ActiveBadge active={i.isActive} />
            </div>
          </div>
        )}
        empty={
          <EmptyState
            icon={<Receipt />}
            title={t('No fee items')}
            description={t('Add fee items for this academic year to start billing.')}
            compact
          />
        }
      />
      <p className="border-t border-line px-5 py-3 text-xs text-muted">
        {t(
          'Billing rules: {{rules}} — monthly items are billed in every month run; per-term items in the first month of each 4-month term (e.g. September, January, May); yearly and one-time items in the annual run.',
          { rules: FEE_FREQUENCIES.map((f) => t(frequencyLabel[f])).join(' · ') },
        )}
      </p>
      {editing ? (
        <FeeItemDialog
          open
          onOpenChange={() => setEditing(null)}
          record={editing.record}
          defaultYearId={yearId}
        />
      ) : null}
      {deleting ? (
        <ConfirmDialog
          open
          onOpenChange={() => setDeleting(null)}
          title={t('Delete “{{name}}”?', { name: deleting.name })}
          description={t('This item has never been invoiced, so it can be removed permanently.')}
          confirmLabel={t('Delete')}
          loading={remove.isPending}
          onConfirm={() =>
            remove.mutate(deleting.id, {
              onSuccess: () => {
                toast.success(t('Fee item deleted'), deleting.name)
                setDeleting(null)
              },
              onError: (e) => toast.error(t('Could not delete'), errorMessage(e)),
            })
          }
        />
      ) : null}
    </Card>
  )
}

function DiscountsTab() {
  const { t } = useTranslation('finance')
  const { can } = useAuth()
  const { money } = useMoney()
  const list = useQuery(discountsQuery())
  const remove = useDeleteDiscount()
  const [editing, setEditing] = useState<Editing<FeeDiscount>>(null)
  const [deleting, setDeleting] = useState<FeeDiscount | null>(null)
  const manage = can('finance.manage')

  const columns: Column<FeeDiscount>[] = [
    { key: 'name', header: t('Name'), cell: (d) => <span className="font-medium text-ink">{d.name}</span> },
    {
      key: 'type',
      header: t('Type'),
      hideBelow: 'sm',
      cell: (d) => (d.type === 'percentage' ? t('Percentage') : t('Fixed amount')),
    },
    {
      key: 'value',
      header: t('Value'),
      align: 'right',
      cell: (d) => (
        <span className="tabular">
          {d.type === 'percentage' ? formatPercent(d.value, 2) : money(d.value)}
          {d.maxAmount ? (
            <span className="ml-1 text-xs text-muted">
              {t('max {{amount}}', { amount: money(d.maxAmount) })}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'students',
      header: t('Students'),
      align: 'right',
      hideBelow: 'md',
      cell: (d) => <span className="tabular">{d.studentCount}</span>,
    },
    { key: 'status', header: t('Status'), cell: (d) => <ActiveBadge active={d.isActive} /> },
    ...(manage
      ? [
          {
            key: 'actions',
            header: <span className="sr-only">{t('Actions')}</span>,
            align: 'right' as const,
            cell: (d: FeeDiscount) => (
              <div className="flex justify-end gap-1">
                <IconButton
                  label={t('Edit {{name}}', { name: d.name })}
                  icon={<Pencil />}
                  onClick={() => setEditing({ record: d })}
                />
                <IconButton
                  label={
                    d.studentCount > 0
                      ? t('{{name}} is assigned to students', { name: d.name })
                      : t('Delete {{name}}', { name: d.name })
                  }
                  icon={<Trash2 />}
                  disabled={d.studentCount > 0}
                  onClick={() => setDeleting(d)}
                />
              </div>
            ),
          },
        ]
      : []),
  ]

  return (
    <Card>
      <CardHeader
        title={t('Discounts')}
        description={t(
          'Assign a discount to a student from their ledger; it applies to invoices generated afterwards.',
        )}
        icon={<BadgePercent />}
        actions={
          manage ? (
            <Button size="sm" leftIcon={<Plus />} onClick={() => setEditing({ record: null })}>
              {t('New discount')}
            </Button>
          ) : null
        }
      />
      <DataTable
        caption={t('Fee discounts')}
        columns={columns}
        rows={list.data}
        getRowId={(d) => d.id}
        loading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        skeletonRows={4}
        empty={
          <EmptyState
            icon={<BadgePercent />}
            title={t('No discounts yet')}
            description={t('Create sibling, staff or scholarship discounts.')}
            compact
          />
        }
      />
      {editing ? <DiscountDialog open onOpenChange={() => setEditing(null)} record={editing.record} /> : null}
      {deleting ? (
        <ConfirmDialog
          open
          onOpenChange={() => setDeleting(null)}
          title={t('Delete “{{name}}”?', { name: deleting.name })}
          description={t('Invoices already issued keep the discount they received.')}
          confirmLabel={t('Delete')}
          loading={remove.isPending}
          onConfirm={() =>
            remove.mutate(deleting.id, {
              onSuccess: () => {
                toast.success(t('Discount deleted'), deleting.name)
                setDeleting(null)
              },
              onError: (e) => toast.error(t('Could not delete'), errorMessage(e)),
            })
          }
        />
      ) : null}
    </Card>
  )
}

export function FeeSetupPage() {
  const { t } = useTranslation('finance')
  useDocumentTitle('Fee setup')
  const [params, setParams] = useSearchParams()
  const raw = params.get('tab')
  const tab: Tab = raw === 'items' || raw === 'discounts' ? raw : 'categories'

  return (
    <>
      <PageHeader
        title={t('Fee setup')}
        description={t('Categories, billable fee items and discounts used when invoices are generated.')}
        breadcrumbs={[{ label: t('Finance') }, { label: t('Fee setup') }]}
        homeHref="/admin"
      />
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v }, { replace: true })}>
        <TabsList aria-label={t('Fee setup sections')}>
          <TabsTrigger value="categories" icon={<FolderTree />}>
            {t('Categories')}
          </TabsTrigger>
          <TabsTrigger value="items" icon={<Receipt />}>
            {t('Fee items')}
          </TabsTrigger>
          <TabsTrigger value="discounts" icon={<BadgePercent />}>
            {t('Discounts')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="categories">
          <CategoriesTab />
        </TabsContent>
        <TabsContent value="items">
          <ItemsTab />
        </TabsContent>
        <TabsContent value="discounts">
          <DiscountsTab />
        </TabsContent>
      </Tabs>
    </>
  )
}
